import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function ContractsPage(): React.JSX.Element {
  const [page, setPage] = useState(1);
  const contracts = useQuery({
    queryKey: ['contracts', 'list', page],
    queryFn: () => applicationsApi.listContractsForCustomer({ page, pageSize: 10 }),
  });

  const items = contracts.data?.items ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Contracts</h1>
        <p className={styles.pageSubtitle}>Approved and active repayment contracts.</p>
      </header>

      <section className={styles.card} aria-labelledby="contracts-heading">
        <h2 className={styles.cardTitle} id="contracts-heading">
          Your contracts
        </h2>

        {contracts.isPending && <PageSpinner label="Loading contracts" />}
        {contracts.isError && <Alert tone="error">{toDisplayMessage(contracts.error)}</Alert>}

        {contracts.isSuccess && items.length === 0 && (
          <p className={styles.empty}>No contracts yet.</p>
        )}

        {contracts.isSuccess && items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Status</th>
                  <th scope="col">Financed</th>
                  <th scope="col">Instalment</th>
                </tr>
              </thead>
              <tbody>
                {items.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <Link to={routeTo.customerContract(contract.id)}>
                        {contract.contractReference ?? 'Offer awaiting acceptance'}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge status={contract.status} />
                    </td>
                    <td>{formatNgn(contract.financedAmount)}</td>
                    <td>{formatNgn(contract.instalmentAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {contracts.isSuccess && contracts.data.pagination.totalPages > 1 && (
          <div className={styles.actions} aria-label="Contract pages">
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <span>
              Page {contracts.data.pagination.page} of {contracts.data.pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={page >= contracts.data.pagination.totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
